import { getLexiconEntry } from '@tunasong/music-lib'
import type { ApplicabilityReason, Substitution } from '@tunasong/music-lib'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './reharm-substitution-card.module.css'

const APPLICABILITY_CAPTIONS: Record<ApplicabilityReason, string> = {
  'role-mismatch': "Doesn't apply at this chord's role.",
  'slot-position': 'Needs a target chord after the current slot.',
  'next-chord-mismatch': "Next chord doesn't match what this technique needs.",
  'non-diatonic': 'Only applies to diatonic chords.',
}

export interface ReharmSubstitutionCardProps {
  substitution: Substitution
  isActive: boolean
  onApply: () => void
}

/**
 * One substitution-technique card inside the Reharm Lab side panel. Renders
 * the technique name, level-resolved teaching copy, and an Apply button.
 * Active state shows "Applied"; unavailable state disables the button and
 * shows a short applicability caption explaining why.
 */
export const ReharmSubstitutionCard: FC<ReharmSubstitutionCardProps> = props => {
  const { substitution, isActive, onApply } = props
  const entry = getLexiconEntry(substitution.lexiconId)
  const copy = useTheoryCopy(substitution.lexiconId)
  const techniqueName = entry?.displayNameShort ?? entry?.displayName ?? substitution.technique

  const buttonLabel = isActive ? 'Applied' : 'Apply'
  const caption =
    !substitution.available && substitution.applicabilityReason
      ? APPLICABILITY_CAPTIONS[substitution.applicabilityReason]
      : null

  return (
    <article className={`${styles.card} ${isActive ? styles.cardActive : ''}`} data-active={isActive}>
      <header className={styles.header}>
        <h3 className={styles.title}>{techniqueName}</h3>
        <button
          type="button"
          className={styles.applyButton}
          onClick={onApply}
          disabled={!substitution.available}
          aria-pressed={isActive}
        >
          {buttonLabel}
        </button>
      </header>
      {copy?.body ? <p className={styles.copy}>{copy.body}</p> : null}
      {caption ? <p className={styles.caption}>{caption}</p> : null}
    </article>
  )
}
